import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import LoopIcon from '@mui/icons-material/Loop'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, InputAdornment, Typography } from "@mui/material"
import { fileUrl } from "api/api"
import TreatmentsAPI from 'api/dentists/treatment.api'
import InstallmentApi, { FindByPatientInstallment } from "api/finances/installment.api"
import { showModal } from 'app/slice/modal.slice'
import Breadcrumb, { BreadcrumbType } from "component/Breadcrumb/Breadcrumb"
import Loading from "component/Loading"
import SearchPatients from "component/SearchPatients"
import TableCustom from "component/Table/TableCustom"
import TableRowCommon from "component/Table/TableRowCommon"
import BtnProfile from "component/Treatments/BtnProfile"
import PopupPatientDetail from "component/Treatments/PopupPatientDetail"
import { imgs } from "constants/images"
import { colors } from "constants/theme"
import * as TreatmentsUseStyled from 'features/treatments/UseStyle'
import _ from "lodash"
import moment from "moment"
import MainButton from "new-components/buttons/main-button"
import DateRangeInput from "new-components/inputs/date-range-input"
import TextInput from "new-components/inputs/text-input"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { routeName } from "routes/routes-name"
import { numberFormat } from "utils/app.utils"
import { dateToView } from "utils/date.utils"
import { SummaryValue } from "../overdue/overdueDetail"
import PopInstallmentChange from "./pop-installment-change"
import PopInstallmentDetail from "./pop-installment-detail"

const InstallmentDetail = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const { id }: { id: string } = useParams()
    const [loading, setLoading] = useState(true)
    const [loadingPatient, setLoadingPatient] = useState(true)
    const history = useHistory()

    const [patientId, setPatientId] = useState(0)
    const [patient, setPatient]: any = useState({})
    const [isShowPopupDetail, setIsShowPopupDetail] = useState<boolean>(false)
    const breadcrumbs: BreadcrumbType[] = [{ title: t('INSTALLMENT.TITLE'), path: routeName.installment }, { title: 'INSTALLMENT.DETAIL.TITLE' }]
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('createAt')
    const [sortType, setSortType] = useState('DESC')
    const [search, setSearch] = useState('')
    const [rangeDate, setRangeDate] = useState(['', ''])

    const [dataInstallment, setDataInstallment] = useState<any>([])
    const [countPendingList, setCountPendingList] = useState(0)
    const [totalPendingPaid, setTotalPendingPaid] = useState(0)

    const [popInstallmentChange, setPopInstallmentChange] = useState<{ data: any, open: boolean }>({ data: {}, open: false })
    const [popInstallmentDetail, setPopInstallmentDetail] = useState<{ data: any, open: boolean }>({ data: {}, open: false })

    const getPatientId = (id: number) => {
        setPatientId(id)
        loadPatient(id)
    }

    const loadPatient = async (valuePatientId: number) => {
        setPatient({})
        setLoadingPatient(true)
        if (valuePatientId) {
            setPatientId(valuePatientId)
            const patientData = await TreatmentsAPI.findPatient(valuePatientId).finally(() => setLoadingPatient(false))
            if (patientData.status === 200 && !_.isEmpty(patientData.data)) setPatient(patientData.data)
        } else {
            setPatientId(0)
            setLoadingPatient(false)
        }
    }

    const handleClear = () => {
        setPatientId(0)
        setPatient({})
        setRowCount(0)
        setDataInstallment([])
    }

    const loadInstallmentDetail = async (id: number) => {
        setLoading(true)
        const condition: FindByPatientInstallment = {
            patientId: id,
            page: page,
            pageLimit: pageLimit,
            sortBy: sortBy,
            sortType: sortType,
            search: search,
            dateStart: rangeDate[0] || '',
            dateEnd: rangeDate[1] || ''
        }
        await InstallmentApi.findByPatient(condition).then((res: any) => {
            if (res?.data) {
                setRowCount(Number(res?.headers['x-total'] || 0))
                setDataInstallment(res.data?.list || [])
                setCountPendingList(res.data?.sum[0]?.countPendingList || 0)
                setTotalPendingPaid(res.data?.sum[0]?.totalPendingPaid || 0)
            }
        }).finally(() => setLoading(false))
    }

    useEffect(() => {
        if (!Number(id) || isNaN(Number(id))) {
            history.push(`${routeName.installment}`)
        } else {
            loadPatient(Number(id))
        }
    }, [])

    useEffect(() => {
        loadInstallmentDetail(patientId)
    }, [page, pageLimit, patientId])

    const onViewHistory = () => {
        dispatch(showModal())
        setIsShowPopupDetail(true)
    }

    const onOpenInstallmentChange = (objData: any) => {
        setPopInstallmentChange({ data: objData, open: true })
    }

    const onCloseInstallmentChange = () => {
        setPopInstallmentChange({ data: {}, open: false })
    }

    const onOpenInstallmentDetail = (objData: any) => {
        setPopInstallmentDetail({ data: objData, open: true })
    }

    const onCloseInstallmentDetail = () => {
        setPopInstallmentDetail({ data: {}, open: false })
    }

    const renderImage = (img: any) => {
        if (!_.isEmpty(img) && !_.isEmpty(img.filePath) && !_.isEmpty(img.fileName)) return `${fileUrl}/${img.filePath}/${img.fileName}`
        else return imgs.defaultAvatar
    }

    const headCells = [
        { id: 'no', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.NUMBER'), width: '50px', align: 'center', class: 'text-nowrap ' },
        { id: 'operativeName', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.SERVICE'), align: 'left', class: 'text-nowrap ' },
        { id: 'installmentPaidDate', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.INSTALLMENT_PAID_DATE'), align: 'center', width: '180px', class: 'text-nowrap ' },
        { id: 'totalPrice', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.TOTAL_PRICE'), align: 'right', width: '180px', class: 'text-nowrap ' },
        { id: 'totalPaid', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.TOTAL_PAID'), align: 'right', width: '180px', class: 'text-nowrap ' },
        { id: 'installmentTotal', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.INSTALLMENT_TOTAL'), align: 'center', width: '160px', class: 'text-nowrap ' },
        { id: 'status', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.STATUS'), align: 'center', width: '160px', class: 'text-nowrap ' },
        { id: 'updatedBy', disablePadding: false, label: t('INSTALLMENT.DETAIL.TABLE.UPDATEDBY'), align: 'center', width: '160px', class: 'text-nowrap ' },
        { id: 'action', disablePadding: false, label: 'Action', class: 'text-nowrap ' }
    ]

    const renderDataInstallment = (objData: any, index: number) => {
        index = page * pageLimit - pageLimit + index + 1

        const { installmentPaymentId, installmentPaidDate, totalPaid, totalPrice,
            installmentTotal, updatedAt, updatedBy, operativeName, status } = objData

        const renderStatus = () => {
            const colorStatus = status === 'PAID' ? colors.themeSecondColor : status === 'CANCEL' ? '#808080' : colors.statusInactive
            return (
                <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                    <Box height={'18px'} width={'18px'} borderRadius={'50%'} sx={{ backgroundColor: colorStatus }} />
                    <Box sx={{ textWrap: 'nowrap' }}>{t(`INSTALLMENT.STATUS.${status}`)}</Box>
                </Box>
            )
        }

        const isDisabled = status === 'PAID' || status === 'CANCEL'

        const objRenderData = {
            key: installmentPaymentId,
            id: installmentPaymentId,
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: index, class: 'text-nowrap ' },
                { option: 'TEXT', align: 'left', label: operativeName || '-', class: 'text-nowrap ' },
                { option: 'TEXT', align: 'center', label: installmentPaidDate ? dateToView(installmentPaidDate) : '-', class: 'text-nowrap ' },
                { option: 'TEXT', align: 'right', label: numberFormat(totalPrice) || '-', class: 'text-nowrap ' },
                { option: 'TEXT', align: 'right', label: numberFormat(totalPaid) || '-', class: 'text-nowrap ' },
                { option: 'TEXT', align: 'center', label: installmentTotal || '0', class: 'text-nowrap ' },
                {
                    option: 'COMPONENT',
                    component: (
                        <Box textAlign={'center'}>
                            {renderStatus()}
                        </Box>
                    )
                },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt }, class: 'text-nowrap ' },
                {
                    option: 'COMPONENT',
                    component: (
                        <Box display={'flex'} alignContent={'center'} gap={'8px'}>
                            <MainButton
                                title={t('INSTALLMENT.DETAIL.BUTTON.DETAIL')}
                                onClick={() => onOpenInstallmentDetail(objData)}
                                type="primary"
                                variant="outlined"
                                style={{ height: '40px', textWrap: 'nowrap', width: '100%', padding: '8px' }}
                                startIcon={<VisibilityIcon sx={{ color: colors.themeSecondColor }} />}
                            />
                            <MainButton
                                title={t('INSTALLMENT.DETAIL.BUTTON.CHANGE')}
                                onClick={() => onOpenInstallmentChange(objData)}
                                type="primary"
                                variant="contained"
                                style={{ height: '40px', textWrap: 'nowrap', width: '100%', padding: '8px 18px' }}
                                startIcon={<LoopIcon sx={{ color: isDisabled ? '#858585' : '#fff', transform: 'rotate(90deg)' }} />}
                                disabled={isDisabled}
                            />
                        </Box>
                    )
                },
            ]
        }

        return (<TableRowCommon {...objRenderData} />)
    }

    return (
        <Box padding={"16px 16px 0 16px"}>
            <Loading show={loading || loadingPatient} />
            <Breadcrumb breadcrumbs={breadcrumbs} />
            <Typography sx={{ fontSize: 20, fontWeight: 500 }}>{t('INSTALLMENT.DETAIL.TITLE')}</Typography>
            <Box className="mt-4" sx={{ width: { md: '80%', lg: '70%', xl: '45%' } }}>
                <SearchPatients
                    status="ACTIVE"
                    callbackPatientId={getPatientId}
                    patient={patient?.firstname ? patient : { firstname: '', lastname: '' }}
                    patientId={patientId}
                    onClear={handleClear}
                />
            </Box>
            <TreatmentsUseStyled.CardProfile className={`d-flex px-3 mt-3 ${!patient.patientId ? 'pe-none' : ''}`} onClick={onViewHistory}>
                <BtnProfile disabled={!patient.patientId} profile={renderImage(patient.profile)} branchCnNumber={patient.branchCnNumber} firstName={patient.firstname} lastName={patient.lastname} firstnameEn={patient.firstnameEn} lastnameEn={patient.lastnameEn} displayName={patient.displayName} rankId={patient.rankId} lastVisit={patient.updatedAt} />
            </TreatmentsUseStyled.CardProfile>
            <PopupPatientDetail type="overdue" profile={renderImage(patient.profile)} patient={patient} showPopup={isShowPopupDetail} onClose={() => setIsShowPopupDetail(false)} />

            <Box display={'flex'} justifyContent={'space-between'} width={'100%'} marginTop={'16px'} flexWrap={'wrap'} gap={'8px 0'}>
                <Box display={'flex'} alignItems={'center'} gap={'8px'} width={{ sm: 'auto', xs: '100%' }} flexWrap={'wrap'}>
                    <Box width={{ sm: '300px', xs: 'calc(50% - 8px)' }} >
                        <TextInput
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            label={t('INSTALLMENT.DETAIL.SEARCH')}
                            value={search}
                            size='large'
                            fullWidth
                            inputProps={{
                                value: search,
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');

                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box width={{ sm: '300px', xs: 'calc(50% - 8px)' }}>
                        <DateRangeInput
                            inputHeight={40}
                            value={rangeDate || ['', '']}
                            onchange={(val: any) => {
                                if (_.isEmpty(val)) setRangeDate(['', ''])
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                            }}
                            label={''}
                            allowClear
                            onClear={() => setRangeDate(['', ''])}
                        />
                    </Box>
                    <Box width={{ sm: '120px', xs: '100%' }} sx={{ button: { width: '100% ' } }}>
                        <MainButton
                            title={t('INSTALLMENT.BUTTON.SEARCH')}
                            onClick={() => {
                                setPage(1)
                                loadInstallmentDetail(patientId)
                            }}
                            type="primary"
                            variant="contained"
                            style={{ height: '40px' }}
                        />
                    </Box>
                </Box>
                <Box width={{ md: 'auto', xs: '100%' }} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SummaryValue >
                        <Box style={{ fontWeight: 'bold', fontSize: 20 }}>
                            {countPendingList} {t('INSTALLMENT.DETAIL.LIST')}
                        </Box>
                        <Box>{t('INSTALLMENT.DETAIL.LIST_SUB')}</Box>
                    </SummaryValue>
                    <SummaryValue>
                        <Box style={{ fontWeight: 'bold', fontSize: 20 }}>
                            {numberFormat(totalPendingPaid)} {t('INSTALLMENT.DETAIL.BAHT')}
                        </Box>
                        <Box>{t('INSTALLMENT.DETAIL.PAID_SUB')}</Box>
                    </SummaryValue>
                </Box>
            </Box>
            <Box marginTop={'16px'}>
                <TableCustom
                    page={page}
                    pageLimit={pageLimit}
                    sortType={sortType}
                    sortBy={sortBy}
                    onSort={(sortByVal: string, sortTypeVal: string) => {
                        setSortType(sortTypeVal)
                        setSortBy(sortByVal)
                    }}
                    setPageLimit={(e) => setPageLimit(e)}
                    setPage={(e) => setPage(e)}
                    rowCount={rowCount}
                    headCells={headCells}
                    rowsData={dataInstallment?.length ? dataInstallment.map((item: any, index: number) => renderDataInstallment(item, index)) : []}
                />
            </Box>

            {popInstallmentChange.open &&
                <PopInstallmentChange
                    {...popInstallmentChange}
                    onClose={onCloseInstallmentChange}
                    onReload={() => loadInstallmentDetail(patientId)}
                />
            }

            {popInstallmentDetail &&
                <PopInstallmentDetail
                    {...popInstallmentDetail}
                    onClose={onCloseInstallmentDetail}
                />
            }

        </Box>
    )
}

export default InstallmentDetail